/**
 * @license Copyright (c) 2003-2020, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

// The editor creator to use.
import ClassicEditorBase from "@ckeditor/ckeditor5-editor-classic/src/classiceditor";

import Essentials from "@ckeditor/ckeditor5-essentials/src/essentials";
import Autoformat from "@ckeditor/ckeditor5-autoformat/src/autoformat";
import Bold from "@ckeditor/ckeditor5-basic-styles/src/bold";
import Italic from "@ckeditor/ckeditor5-basic-styles/src/italic";
import Underline from "@ckeditor/ckeditor5-basic-styles/src/underline";
import BlockQuote from "@ckeditor/ckeditor5-block-quote/src/blockquote";
// import CKFinder from '@ckeditor/ckeditor5-ckfinder/src/ckfinder';
import Heading from "@ckeditor/ckeditor5-heading/src/heading";
import Image from "@ckeditor/ckeditor5-image/src/image";
import ImageCaption from "@ckeditor/ckeditor5-image/src/imagecaption";
import ImageStyle from "@ckeditor/ckeditor5-image/src/imagestyle";
import ImageToolbar from "@ckeditor/ckeditor5-image/src/imagetoolbar";
import ImageUpload from "@ckeditor/ckeditor5-image/src/imageupload";
import Indent from "@ckeditor/ckeditor5-indent/src/indent";
import IndentBlock from '@ckeditor/ckeditor5-indent/src/indentblock.js';
import Link from "@ckeditor/ckeditor5-link/src/link";
import { DocumentList }  from '@ckeditor/ckeditor5-list';
import MediaEmbed from "@ckeditor/ckeditor5-media-embed/src/mediaembed";
import MediaEmbedToolbar from "@ckeditor/ckeditor5-media-embed/src/mediaembed";
import Paragraph from "@ckeditor/ckeditor5-paragraph/src/paragraph";
import PasteFromOffice from "@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice";
import Table from "@ckeditor/ckeditor5-table/src/table";
import TableToolbar from "@ckeditor/ckeditor5-table/src/tabletoolbar";
import TableColumnResize from "@ckeditor/ckeditor5-table/src/tablecolumnresize";
import TextTransformation from "@ckeditor/ckeditor5-typing/src/texttransformation";
import HorizontalLine from '@ckeditor/ckeditor5-horizontal-line/src/horizontalline';
import Alignment from "@ckeditor/ckeditor5-alignment/src/alignment";
import ImageResize from "@ckeditor/ckeditor5-image/src/imageresize";
import LinkImage from "@ckeditor/ckeditor5-link/src/linkimage";
import FontColor from "@ckeditor/ckeditor5-font/src/fontcolor";
import MediaEmbedCommand from '@ckeditor/ckeditor5-media-embed/src/mediaembedcommand.js'
import {LivePosition, LiveRange} from 'ckeditor5/src/engine';
import Divider from "./divider/divider";
import SectionColorsBox from "./section-colors-box/section-colors-box";
import SectionColorsToolbar from "./section-colors-box/section-colors-toolbar";
import Blocks from "./blocks/blocks"
import AnnouncementBox from "./people-announcement/announcement-box";
import AnnouncementBoxToolbar from "./people-announcement/announcement-box-toolbar";
import ImageAndTextBox from "./image-and-text/image-and-text-box";
import ImageAndTextToolbar from "./image-and-text/image-and-text-box-toolbar";

import CKEditorInspector from '@ckeditor/ckeditor5-inspector';
import ImageAndQuoteBox from "./image-and-quote/image-and-quote-box";
import ImageAndQuoteToolbar from "./image-and-quote/image-and-quote-box-toolbar";
import MovieAndQuoteBox from "./movie-and-text/movie-and-text-box";
import MovieAndQuoteToolbar from "./movie-and-text/movie-and-text-box-toolbar";
import TextBox from "./text/text-box";
import TextBoxToolbar from "./text/text-box-toolbar";
import ImagesAndTextBox from "./images-and-text/images-and-text-box";
import ImagesBox from "./images/images-box";
import ImagesBoxToolbar from "./images/images-toolbar";
import MyImageToolbar from "./blocks/image-toolbar";
import CustomMovieToolbar from "./movie-and-text/movie-toolbar";


class NewsEditor extends ClassicEditorBase {
}

// Plugins to include in the build.
NewsEditor.builtinPlugins = [
    Essentials,
    Autoformat,
    Bold,
    Italic,
    Underline,
    BlockQuote,
    // CKFinder,
    Heading,
    Image,
    ImageCaption,
    ImageStyle,
    ImageToolbar,
    ImageUpload,
    Indent,
    IndentBlock,
    Link,
    DocumentList,
    MediaEmbed,
    Paragraph,
    PasteFromOffice,
    Table,
    TableToolbar,
    TableColumnResize,
    TextTransformation,
    Alignment,
    ImageResize,
    LinkImage,
    HorizontalLine,
    FontColor,
    Divider,
    SectionColorsBox,
    SectionColorsToolbar,
    MediaEmbedToolbar,
    Blocks,
    AnnouncementBox,
    AnnouncementBoxToolbar,
    ImageAndTextBox,
    ImageAndTextToolbar,
    ImageAndQuoteBox,
    ImageAndQuoteToolbar,
    MovieAndQuoteBox,
    MovieAndQuoteToolbar,
    TextBox,
    TextBoxToolbar,
    ImagesAndTextBox,
    ImagesBox,
    ImagesBoxToolbar,
    MyImageToolbar,
    CustomMovieToolbar
];

// Editor configuration.
NewsEditor.defaultConfig = {
    language: 'en',
    toolbar: {
        items: [
            'heading',
            'bold',
            'link',
            'bulletedList',
            'numberedList',
            '|',
            'outdent',
            'indent',
            'alignment',
            '|',
            'imageUpload',
            'insertTable',
            'mediaEmbed',
            '|',
            'divider',
            '|',
            'blocks',
            'sectionColorsBox',
            '|',
            'undo',
            'redo'

        ],
        shouldNotGroupWhenFull: true
    },
    image: {
        toolbar: [
            "imageStyle:block",
            "imageStyle:side",
            'imageStyle:inline',
            'imageStyle:wrapText',
            'imageStyle:breakText',
            "|",
            "toggleImageCaption",
            "imageTextAlternative",
            "resizeImage"
        ],
    },
    resizeOptions: [
        {
            name: 'resizeImage:original',
            value: null,
            label: 'Original'
        },
        {
            name: 'resizeImage:33',
            value: '33',
            label: '33%'
        },
        {
            name: 'resizeImage:50',
            value: '50',
            label: '50%'
        },
        {
            name: 'resizeImage:66',
            value: '66',
            label: '66%'
        }
    ],
    list: {
        properties: {
            styles: true,
            startIndex: true,
            reversed: true
        }
    },
    table: {
        contentToolbar: ["tableColumn", "tableRow", "mergeTableCells"],
    },
    customImage: {
        options: [
            {
                name: 'resizeImage:33',
                value: '33',
                label: '33%'
            },
            {
                name: 'resizeImage:50',
                value: '50',
                label: '50%'
            },
            {
                name: 'resizeImage:66',
                value: '66',
                label: '66%'
            }
        ]
    },
    sectionColorsBox: {
        contentToolbar: ["sectionColorsBox"],
        options: [
            {
                model: '',
            },
            {
                model: 'bg-neutral-dark-colour',
            },
            {
                model: 'bg-neutral-medium-colour',
            },
            {
                model: 'bg-neutral-light-colour',
            },
            {
                model: 'bg-neutral-gradient',
            },
            {
                model: 'bg-neutral-gradient-bottom-right',
            },
            {
                model: 'bg-neutral-gradient-top-left',
            },
            {
                model: 'bg-blue-gradient-top-left',
            },
            {
                model: 'bg-blue-gradient-bottom-right',
            },
            {
                model: 'bg-gradient-white-to-grey',
            },
            {
                model: 'bg-warm-blue-gradient-top-left',
            },
            {
                model: 'bg-warm-blue-gradient-bottom-right',
            }
        ],
    },

    heading: {
        options: [
            {
                model: 'heading-1-rds',
                view: {
                    name: 'h1',
                    classes: ['article-heading-1']
                },
                title: 'Heading 1',
                class: 'article-heading-1',
                converterPriority: 'high'
            },
            {
                model: 'heading-2-rds',
                view: {
                    name: 'h2',
                    classes: ['article-heading-2']
                },
                title: 'Heading 2',
                class: 'article-heading-2',
                converterPriority: 'high'
            },
            {
                model: 'heading-3-rds',
                view: {
                    name: 'h3',
                    classes: ['article-heading-3']
                },
                title: 'Heading 3',
                class: 'article-heading-3',
                converterPriority: 'high'
            },
            {
                model: 'heading-4-rds',
                view: {
                    name: 'h4',
                    classes: ['article-heading-4']
                },
                title: 'Heading 4',
                class: 'article-heading-4',
                converterPriority: 'high'
            },
            {
                model: 'heading-5-rds',
                view: {
                    name: 'h5',
                    classes: ['article-heading-5']
                },
                title: 'Heading 5',
                class: 'article-heading-5',
                converterPriority: 'high'
            },
            {
                model: 'heading-6-rds',
                view: {
                    name: 'h6',
                    classes: ['article-heading-6']
                },
                title: 'Heading 6',
                class: 'article-heading-6',
                converterPriority: 'high'
            },
            {
                model: 'quote-m',
                view: {
                    name: 'blockquote',
                    classes: ['article-quote-m']
                },
                title: 'Quote M',
                class: 'article-quote-m',
                converterPriority: 'high'
            },
            {
                model: 'quote-s',
                view: {
                    name: 'blockquote',
                    classes: ['article-quote-s']
                },
                title: 'Quote S',
                class: 'article-quote-s',
                converterPriority: 'high'
            },
            {
                model: 'lead-rds',
                view: {
                    name: 'p',
                    classes: ['article-lead']
                },
                title: 'Lead',
                class: 'article-lead',
                converterPriority: 'high'
            },

            {
                model: 'body-s-rds',
                view: {
                    name: 'p',
                    classes: ['article-body-s']
                },
                title: 'Body S',
                class: 'article-body-s'
            },
            {
                model: 'paragraph',
                view: {
                    name: 'p',
                    classes: ['article-body-m']
                },
                title: 'Body M',
                class: 'article-body-m'
            }
        ]
    },
    link: {
        addTargetToExternalLinks: true,
        defaultProtocol: 'https://',
        decorators: [
            {
                mode: 'automatic',
                callback: url => /^(https?:)?\/\//.test(url),
                attributes: {
                    class: 'rds-link'
                }
            }
        ]
    },
    mediaEmbed: {
        elementName: 'oembed',
        extraProviders: [
            {
                name: 'streamingMedia',
                url: [
                    /^(https:\/\/)streamingmedia\.roche\.com\/(.*)/
                ],
                html: match => {
                    const data = match[2].includes('?') ? match[2].split('?')[0].split('/') : match[2].split('/');
                    const params = match[2].includes('?') ? match[2].split('?')[1].split('&') : [];
                    let title;
                    let entryId;
                    let src;
                    switch (data[0]) {
                        // ['media', /TITLE/, /ENTRY_ID/]
                        case 'media': {
                            title = data[1].split('+').join(' ');
                            entryId = data[2];
                            src = `https://streamingmedia.roche.com/embed/secure/iframe/entryId/${entryId}/uiConfId/45631991`;
                            break;
                        }
                        case 'playlist': {
                            // ['playlist', 'dedicated', /PBC/, /???/, /ENTRY_ID/]
                            title = '';
                            entryId = data[4];
                            src = `https://streamingmedia.roche.com/embed/secure/iframe/entryId/${entryId}/uiConfId/45631991`;
                            break;
                        }
                        case 'embed': {
                            // ['embed', 'secure', 'iframe', 'entryId', /ENTRY_ID/, 'uiConfId', CONF_ID, 'pbc', /PBC/]
                            title = '';
                            entryId = data[4];
                            src = `https://streamingmedia.roche.com/embed/secure/iframe/entryId/${entryId}/uiConfId/45631991`;
                            break;
                        }
                    }

                    if (params.length > 0) {
                        params.forEach(param => {
                            src += `/${param.split('=')[0]}/${param.split('=')[0]}`
                        })
                    } else {
                        src += '/st/0'
                    }

                    return (
                        `<div style="position: relative; padding-bottom: 100%; height: 0; padding-bottom: 56.2493%;">
						<iframe
            id="kmsembed-${entryId}"
            src="${src}"
            style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;"
            allowfullscreen webkitallowfullscreen mozAllowFullScreen
            allow="autoplay *;fullscreen *; encrypted-media *" 
            referrerPolicy="no-referrer-when-downgrade"
            sandbox="allow-downloads allow-forms allow-same-origin allow-scripts allow-top-navigation allow-pointer-lock allow-popups allow-modals allow-orientation-lock allow-popups-to-escape-sandbox allow-presentation allow-top-navigation-by-user-activation"
            frameborder="0"
            title="${title}"></iframe>
						</div>
            `
                    );
                }
            }
        ]
    }
};
export default {NewsEditor, MediaEmbedCommand, LiveRange, LivePosition, CKEditorInspector};
